import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle } from "lucide-react";
import PasswordInput from "@/components/ui/PasswordInput";
import { apiService } from "@/services/api";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function WarmAlert({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-3 py-2 mt-2 shadow-sm text-sm">
      <AlertTriangle className="w-4 h-4 text-orange-400" />
      <span>{message}</span>
    </div>
  );
}

const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const query = useQuery();
  const email = query.get("email") || "";
  const token = query.get("token") || "";

  const [formData, setFormData] = useState({
    password: "",
    password_confirmation: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      await apiService.post('/auth/reset-password', {
        email,
        token,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
      }, { isPublic: true });
      
      setSuccess(true);
      toast({
        title: "Password reset successful",
        description: "You can now log in with your new password.",
      });
      setTimeout(() => navigate("/login"), 2000);
    } catch (error: any) {
      let errorMsg = "Failed to reset password.";
      if (error.response?.data?.message) {
        if (error.response.data.message.toLowerCase().includes("token")) {
          errorMsg = "This password reset link is invalid or has expired.";
        } else if (error.response.data.errors) {
          // Collect all validation errors
          errorMsg = Object.values(error.response.data.errors).flat().join(" ");
        } else {
          errorMsg = error.response.data.message;
        }
      }
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="text-center text-red-600 mt-8">Invalid password reset link.</div>
    );
  }

  return (
    <div className="bg-sauna-beige min-h-screen flex items-center justify-center px-2">
      <Card className="w-full max-w-md min-h-[500px] shadow-2xl border-none rounded-2xl p-0 sm:p-1 bg-white/90 flex flex-col justify-center">
        <CardHeader className="flex flex-col items-center pt-10 pb-6">
          <CardTitle className="text-3xl font-bold text-sauna-navy mb-1 text-center">Reset Password</CardTitle>
          <CardDescription className="text-lg text-sauna-navy/80 mb-4 text-center font-medium">Enter your new password below</CardDescription>
        </CardHeader>
        <CardContent className="px-6 pb-8 pt-0">
          {success ? (
            <div className="text-green-600 text-center font-semibold text-lg py-8">
              Your password has been reset. You can now log in.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <Label htmlFor="password" className="text-sauna-navy font-semibold mb-1 block">New Password</Label>
                <PasswordInput
                  id="password"
                  name="password"
                  placeholder="Enter new password"
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-lg py-3 rounded-xl"
                  required
                />
                {error && formData.password && <WarmAlert message={error} />}
              </div>
              <div>
                <Label htmlFor="password_confirmation" className="text-sauna-navy font-semibold mb-1 block">Confirm New Password</Label>
                <PasswordInput
                  id="password_confirmation"
                  name="password_confirmation"
                  placeholder="Confirm new password"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40 text-lg py-3 rounded-xl"
                  required
                />
                {error && formData.password_confirmation && <WarmAlert message={error} />}
              </div>
              {error && !(formData.password || formData.password_confirmation) && <WarmAlert message={error} />}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-sauna-orange to-sauna-orange/80 text-white text-xl py-3 mt-2 rounded-xl shadow-md hover:from-sauna-orange/90 hover:to-sauna-orange transition font-bold tracking-wide disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordForm; 